import React, {useEffect, useRef, useState} from 'react';
import {useIntl} from 'react-intl';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Link from '@mui/material/Link';
import CircularProgress from '@mui/material/CircularProgress';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RenewalHelpCard from '../components/RenewalHelpCard';
import {useRenewalClick} from '../context/RenewalClickContext';
import {useSigningStatus} from '../hooks/useSigningStatus';
import {fireRenewalJourneyActivity} from '../api/renewalClickApi';
import {RenewalJourneyEvent, RenewalJourneyLocation, SIGNING_RESULT_MESSAGE, DOCUSIGN_EVENT} from '../constants/renewalClick';
import {openSigningTab} from '../utils/signingTab';

// The customer backed out on purpose — no need to show an error.
const SILENT_ABORT_EVENTS = [DOCUSIGN_EVENT.CANCEL, DOCUSIGN_EVENT.DECLINE];

const styles = {
  container: {px: 2, pb: 5, maxWidth: 560, mx: 'auto'},
  title: (theme) => ({
    fontSize: theme.typography.pxToRem(26),
    fontWeight: theme.typography.fontWeightBold,
    color: 'secondary.main',
    textAlign: 'center',
    '&::after': {
      content: '""',
      display: 'inline-block',
      width: '0.22em',
      height: '0.22em',
      borderRadius: '50%',
      backgroundColor: theme.palette.primary.main,
      marginInlineStart: '0.1em',
      verticalAlign: '0.1em',
    },
  }),
  subtitle: (theme) => ({
    fontSize: theme.typography.pxToRem(14.5),
    color: 'text.secondary',
    textAlign: 'center',
    lineHeight: 1.5,
    mt: 1,
    mb: 2.5,
    maxWidth: 300,
    mx: 'auto',
  }),
  card: {
    border: 1,
    borderColor: 'divider',
    borderRadius: '16px',
    p: 2.5,
    mb: 2.5,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 1.5,
    textAlign: 'center',
  },
  loadingBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 1.5,
    p: 4,
    mb: 3,
    textAlign: 'center',
  },
  cta: (theme) => ({
    fontWeight: theme.typography.fontWeightBold,
    fontSize: theme.typography.pxToRem(16),
    py: 1.5,
    borderRadius: '24px',
    width: '100%',
  }),
  note: (theme) => ({
    fontSize: theme.typography.pxToRem(13),
    color: 'text.secondary',
    lineHeight: 1.5,
  }),
  statusText: (theme) => ({
    fontSize: theme.typography.pxToRem(16),
    fontWeight: theme.typography.fontWeightBold,
    color: 'secondary.main',
  }),
  reopen: (theme) => ({
    color: 'secondary.main',
    fontSize: theme.typography.pxToRem(14),
    fontWeight: theme.typography.fontWeightBold,
    textUnderlineOffset: 3,
  }),
  helpCard: {mt: 6, mb: 2.5},
};

const SigningPendingScreen = () => {
  const intl = useIntl();
  const m = (id) => intl.formatMessage({id});
  const {shortLinkKey, opportunityId, setSigningCompleted} = useRenewalClick();

  const [sessionError, setSessionError] = useState(false);
  const [signingOpened, setSigningOpened] = useState(false);
  const [popupBlocked, setPopupBlocked] = useState(false);
  const [finalizing, setFinalizing] = useState(false);

  const {signingCompleted, finalizationFailed} = useSigningStatus(shortLinkKey, true, finalizing);
  const hasTracked = useRef(false);
  const signingTabRef = useRef(null);

  useEffect(() => {
    if (!hasTracked.current && opportunityId) {
      hasTracked.current = true;
      fireRenewalJourneyActivity(opportunityId, RenewalJourneyEvent.REACHED_SIGNATURE, {location: RenewalJourneyLocation.SIGNATURE});
    }
  }, [opportunityId]);

  // Must run synchronously inside the click gesture, or the browser blocks the tab.
  const openSigning = () => {
    signingTabRef.current = openSigningTab(shortLinkKey);
    const opened = Boolean(signingTabRef.current);
    setPopupBlocked(!opened);
    setSigningOpened(opened);
    setSessionError(false);
  };

  // The signing tab reports back: a failed session from /sign, or the DocuSign outcome from /signing-complete.
  useEffect(() => {
    const onMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type !== SIGNING_RESULT_MESSAGE || event.data.shortLinkKey !== shortLinkKey) return;
      if (event.data.event === DOCUSIGN_EVENT.SIGNING_COMPLETE) {
        setFinalizing(true);
        return;
      }
      setSigningOpened(false);
      setSessionError(!SILENT_ABORT_EVENTS.includes(event.data.event));
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [shortLinkKey]);

  // Backend confirmation via SSE/polling → transition to CompletionScreen
  useEffect(() => {
    if (!signingCompleted) return;
    signingTabRef.current?.close();
    setSigningCompleted(true);
  }, [signingCompleted, setSigningCompleted]);

  const helpCard = (
    <RenewalHelpCard
      title={m('renewalClick.helpTitle')}
      description={m('renewalClick.helpDescription')}
      sx={styles.helpCard}
    />
  );

  // Signature is saved at DocuSign but the CRM finalization failed. The SSE stream
  // stays open, so a successful webhook retry still moves us to CompletionScreen.
  if (finalizationFailed && !signingCompleted) {
    return (
      <Box sx={styles.container}>
        <Box sx={styles.card}>
          <ErrorOutlineIcon sx={{fontSize: 44, color: 'error.main'}} />
          <Typography sx={styles.note}>{m('renewalClick.signing.finalizationFailed')}</Typography>
        </Box>
        {helpCard}
      </Box>
    );
  }

  // Signing tab reported completion — waiting for the backend webhook
  if (finalizing) {
    return (
      <Box sx={styles.container}>
        <Box sx={styles.loadingBox}>
          <CircularProgress size={32} />
          <Typography sx={styles.statusText}>{m('renewalClick.signing.finalizing')}</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={styles.container}>
      <Typography sx={styles.title}>{m('renewalClick.signing.title')}</Typography>
      <Typography sx={styles.subtitle}>{m('renewalClick.signing.subtitle')}</Typography>

      {sessionError && (
        <Alert severity='error' sx={{mb: 2}}>{m('renewalClick.signing.sessionError')}</Alert>
      )}

      {popupBlocked && (
        <Alert severity='warning' sx={{mb: 2}}>{m('renewalClick.signing.popupBlocked')}</Alert>
      )}

      <Box sx={styles.card}>
        {signingOpened ? (
          <>
            <CircularProgress size={32} />
            <Typography sx={styles.statusText}>{m('renewalClick.signing.waitingTitle')}</Typography>
            <Typography sx={styles.note}>{m('renewalClick.signing.newTabNote')}</Typography>
            <Link component='button' type='button' onClick={openSigning} sx={styles.reopen}>
              {m('renewalClick.signing.cta')}
            </Link>
          </>
        ) : (
          <>
            <DescriptionOutlinedIcon sx={{fontSize: 44, color: 'primary.main'}} />
            <Typography sx={styles.note}>{m('renewalClick.signing.newTabNote')}</Typography>
            <Button variant='contained' color='primary' onClick={openSigning} startIcon={<OpenInNewIcon />} sx={styles.cta}>
              {m('renewalClick.signing.cta')}
            </Button>
          </>
        )}
      </Box>

      {helpCard}
    </Box>
  );
};

export default SigningPendingScreen;
